<template>
  <div :id="`${source}_cancel_transfer_button`" class="w-full">
    <a-button
      v-if="isVisible"
      @click="toCancelTransfer"
      class="secondary_medium gap-2 w-full"
      :loading="isCancelling"
      :disabled="isCancelling"
    >
      <ThemeImage
        v-if="!isCancelling"
        class="size-3"
        :icon="imageNames.CROSS_GREY"
        alt="cancel_icon"
      />
      {{ isCancelling ? "Cancelling..." : "Cancel Transfer" }}
    </a-button>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { message } from "ant-design-vue";
import store from "@/src/store/index.js";
import CallService from "@/src/api/CallService.js";
import eventBus from "@/src/utils/eventBus.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { conferenceParticipantLabels } from "@/src/constants/callConstants.js";

const props = defineProps({
  source: { type: String, required: true },
});

const isCancelling = ref(false);
let abortController = null;

const onAbort = () => {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
};

onMounted(() => {
  eventBus.on("cancel-transfer-abort", onAbort);
});

onBeforeUnmount(() => {
  eventBus.off("cancel-transfer-abort", onAbort);
});

const isWarmTransferEnabled = computed(
  () => store.getters["conferenceCall/isWarmTransfer"],
);
const isTransferTargetActive = computed(
  () => store.getters["conferenceCall/transferTargetActive"],
);
const isMerged = computed(() => store.getters["conferenceCall/isMerged"]);

// Cancel is only meaningful while we're trying to reach the transfer target.
// Once the target answers, the agent uses Swap / Merge / Complete instead.
const isVisible = computed(
  () =>
    isWarmTransferEnabled.value &&
    !isTransferTargetActive.value &&
    !isMerged.value,
);

const toCancelTransfer = async () => {
  try {
    isCancelling.value = true;
    abortController = new AbortController();

    const payload = {
      conference_id: store.getters["conferenceCall/conferenceSid"],
    };

    const response = await CallService.cancelTransfer(payload, {
      signal: abortController.signal,
    });
    if (response?.aborted) {
      return;
    }
    if (response?.success) {
      message.success("Transfer cancelled");
      await store.dispatch(
        "conferenceCall/cancelWarmTransfer",
        conferenceParticipantLabels.TRANSFER_TARGET,
      );
    } else {
      message.error(response?.error || "Failed to cancel transfer");
    }
  } catch (error) {
    console.error("An error occurred while cancelling transfer: ", error);
    message.error("Failed to cancel transfer");
  } finally {
    abortController = null;
    isCancelling.value = false;
  }
};
</script>
